export interface SnsPaletteDefinition {
  kind: 'sns';
  colors: [number, number, number][] | [number, number, number, number][];
}

export interface ColorAlphaScalePaletteDefinition {
  kind: 'color_alpha_scale';
  alphas: number[];
}

export type PaletteDefinition = SnsPaletteDefinition | ColorAlphaScalePaletteDefinition;

export const PALETTES: Record<string, PaletteDefinition> = {
  geopotential_rainbow: {
    kind: 'sns',
    // Designed for 500 hPa heights (480–600 gpm):
    // purple → pink → blue → turquoise → green → yellow → orange → red
    colors: [
      // Purple → Pink
      [114, 0, 130],
      [134, 0, 140],
      [160, 0, 150],
      [186, 0, 160],
      [210, 0, 170],
      [235, 0, 180],
      [250, 0, 187],
      // Pink → Blue
      [220, 0, 205],
      [180, 0, 220],
      [130, 0, 235],
      [90, 20, 245],
      [60, 50, 255],
      // Blue → Turquoise
      [40, 90, 255],
      [25, 120, 250],
      [10, 150, 240],
      [0, 170, 225],
      [0, 190, 210],
      [0, 200, 200],
      // Turquoise → Green
      [0, 205, 170],
      [0, 210, 140],
      [0, 215, 110],
      [0, 220, 80],
      [0, 220, 50],
      [0, 220, 0],
      // Green → Yellow
      [90, 220, 0],
      [140, 220, 0],
      [180, 220, 0],
      [210, 220, 0],
      [230, 220, 0],
      [245, 225, 0],
      // Yellow → Orange → Red
      [255, 205, 0],
      [255, 185, 0],
      [255, 165, 0],
      [255, 150, 0],
      [255, 135, 0],
      [252, 110, 0],
      [248, 88, 0],
      [240, 70, 8],
      [230, 50, 20],
      [220, 30, 28],
      [210, 18, 30],
    ],
  },
  Spectral_r_with_pink: {
    kind: 'sns',
    colors: [
      [14, 82, 152],
      [30, 100, 165],
      [46, 118, 175],
      [62, 136, 182],
      [78, 154, 185],
      [95, 171, 185],
      [112, 186, 182],
      [130, 199, 176],
      [148, 210, 168],
      [166, 219, 160],
      [184, 228, 152],
      [202, 236, 145],
      [220, 243, 140],
      [236, 248, 142],
      [248, 251, 150],
      [254, 252, 165],
      [234, 247, 158],
      [240, 249, 167],
      [246, 251, 176],
      [252, 254, 186],
      [255, 251, 184],
      [255, 243, 172],
      [254, 236, 159],
      [254, 229, 147],
      [254, 218, 134],
      [254, 206, 124],
      [253, 195, 114],
      [253, 183, 104],
      [252, 168, 94],
      [250, 152, 87],
      [248, 137, 80],
      [246, 122, 73],
      [242, 105, 68],
      [234, 94, 71],
      [227, 83, 74],
      [220, 72, 76],
      [210, 58, 78],
      [197, 44, 75],
      [184, 30, 72],
      [171, 15, 69],
      [178, 10, 83],
      [189, 9, 98],
      [199, 7, 113],
      [209, 6, 128],
      [219, 4, 143],
      [230, 3, 158],
      [240, 1, 173],
      [250, 0, 187],
    ],
  },
  rainbow: {
    kind: 'sns',
    colors: [
      [118, 16, 255],
      [108, 31, 255],
      [98, 47, 254],
      [88, 62, 253],
      [76, 80, 252],
      [66, 95, 250],
      [56, 109, 249],
      [46, 123, 247],
      [33, 140, 244],
      [24, 152, 242],
      [14, 165, 239],
      [4, 176, 237],
      [7, 187, 234],
      [18, 200, 230],
      [29, 209, 226],
      [39, 218, 222],
      [48, 225, 218],
      [61, 234, 213],
      [70, 239, 209],
      [80, 244, 204],
      [90, 248, 200],
      [100, 251, 195],
      [112, 254, 188],
      [122, 255, 183],
      [132, 255, 178],
      [142, 254, 172],
      [155, 251, 165],
      [164, 248, 159],
      [174, 244, 152],
      [185, 239, 146],
      [194, 234, 140],
      [206, 225, 132],
      [217, 218, 125],
      [226, 209, 118],
      [236, 200, 111],
      [249, 187, 102],
      [255, 176, 95],
      [255, 165, 88],
      [255, 152, 80],
      [255, 140, 73],
      [255, 123, 64],
      [255, 109, 56],
      [255, 95, 48],
      [255, 80, 41],
      [255, 62, 31],
      [255, 47, 24],
      [255, 31, 16],
      [255, 16, 8],
    ],
  },
  gist_rainbow: {
    kind: 'sns',
    colors: [
      [255, 0, 14],
      [255, 13, 0],
      [255, 40, 0],
      [255, 67, 0],
      [255, 99, 0],
      [255, 126, 0],
      [255, 153, 0],
      [255, 180, 0],
      [255, 213, 0],
      [255, 240, 0],
      [243, 255, 0],
      [216, 255, 0],
      [189, 255, 0],
      [157, 255, 0],
      [130, 255, 0],
      [103, 255, 0],
      [76, 255, 0],
      [43, 255, 0],
      [16, 255, 0],
      [0, 255, 11],
      [0, 255, 38],
      [0, 255, 65],
      [0, 255, 97],
      [0, 255, 124],
      [0, 255, 151],
      [0, 255, 177],
      [0, 255, 210],
      [0, 255, 237],
      [0, 246, 255],
      [0, 219, 255],
      [0, 192, 255],
      [0, 160, 255],
      [0, 132, 255],
      [0, 105, 255],
      [0, 78, 255],
      [0, 45, 255],
      [0, 18, 255],
      [9, 0, 255],
      [36, 0, 255],
      [63, 0, 255],
      [96, 0, 255],
      [123, 0, 255],
      [150, 0, 255],
      [177, 0, 255],
      [210, 0, 255],
      [237, 0, 255],
      [255, 0, 246],
      [255, 0, 218],
    ],
  },
  gist_ncar: {
    kind: 'sns',
    colors: [
      [0, 36, 81],
      [0, 73, 34],
      [0, 81, 39],
      [0, 47, 122],
      [0, 7, 222],
      [0, 56, 255],
      [0, 127, 255],
      [0, 192, 255],
      [0, 220, 255],
      [0, 242, 255],
      [0, 254, 222],
      [0, 252, 189],
      [0, 250, 157],
      [0, 252, 94],
      [0, 254, 42],
      [25, 247, 0],
      [57, 229, 0],
      [96, 206, 0],
      [110, 222, 0],
      [120, 239, 0],
      [132, 255, 12],
      [155, 255, 32],
      [182, 255, 56],
      [205, 255, 44],
      [228, 255, 24],
      [250, 250, 4],
      [255, 235, 0],
      [255, 223, 0],
      [255, 211, 3],
      [255, 198, 9],
      [255, 186, 14],
      [255, 137, 8],
      [255, 96, 4],
      [255, 62, 0],
      [255, 38, 0],
      [255, 10, 0],
      [255, 0, 88],
      [255, 0, 177],
      [248, 3, 252],
      [214, 21, 255],
      [172, 41, 255],
      [176, 68, 250],
      [206, 98, 244],
      [235, 128, 238],
      [240, 155, 242],
      [243, 179, 245],
      [247, 202, 248],
      [250, 225, 251],
    ],
  },
  nipy_spectral: {
    kind: 'sns',
    colors: [
      [47, 0, 53],
      [93, 0, 107],
      [122, 0, 139],
      [129, 0, 146],
      [131, 0, 154],
      [77, 0, 160],
      [24, 0, 167],
      [0, 0, 181],
      [0, 0, 205],
      [0, 9, 221],
      [0, 56, 221],
      [0, 103, 221],
      [0, 128, 221],
      [0, 144, 221],
      [0, 155, 215],
      [0, 162, 195],
      [0, 168, 175],
      [0, 170, 157],
      [0, 170, 144],
      [0, 167, 115],
      [0, 161, 61],
      [0, 154, 8],
      [0, 167, 0],
      [0, 180, 0],
      [0, 194, 0],
      [0, 207, 0],
      [0, 223, 0],
      [0, 236, 0],
      [0, 250, 0],
      [44, 255, 0],
      [117, 255, 0],
      [192, 253, 0],
      [212, 247, 0],
      [232, 240, 0],
      [243, 229, 0],
      [251, 213, 0],
      [255, 197, 0],
      [255, 177, 0],
      [255, 157, 0],
      [255, 105, 0],
      [255, 33, 0],
      [249, 0, 0],
      [236, 0, 0],
      [222, 0, 0],
      [214, 0, 0],
      [207, 0, 0],
      [204, 44, 44],
      [204, 124, 124],
    ],
  },
  rocket: {
    kind: 'sns',
    colors: [
      [8, 7, 30],
      [15, 11, 35],
      [22, 14, 40],
      [30, 17, 45],
      [38, 20, 51],
      [46, 22, 56],
      [53, 24, 61],
      [61, 26, 66],
      [70, 28, 71],
      [78, 29, 75],
      [85, 29, 78],
      [93, 30, 81],
      [102, 30, 84],
      [111, 31, 87],
      [120, 30, 88],
      [128, 30, 90],
      [137, 29, 90],
      [147, 28, 91],
      [155, 26, 91],
      [164, 25, 90],
      [172, 23, 89],
      [181, 22, 87],
      [191, 22, 84],
      [198, 24, 81],
      [205, 28, 77],
      [212, 34, 74],
      [219, 42, 69],
      [225, 50, 66],
      [230, 59, 63],
      [233, 68, 61],
      [236, 78, 62],
      [239, 89, 64],
      [241, 99, 69],
      [242, 109, 74],
      [243, 118, 81],
      [244, 128, 89],
      [244, 137, 97],
      [245, 146, 105],
      [245, 154, 113],
      [245, 162, 122],
      [245, 172, 133],
      [246, 180, 142],
      [246, 188, 153],
      [246, 195, 163],
      [247, 205, 176],
      [247, 212, 187],
      [248, 219, 198],
      [249, 227, 209],
    ],
  },
  mako: {
    kind: 'sns',
    colors: [
      [16, 7, 11],
      [21, 11, 19],
      [26, 15, 26],
      [31, 18, 33],
      [37, 23, 43],
      [42, 26, 50],
      [46, 30, 58],
      [50, 34, 67],
      [54, 38, 77],
      [56, 42, 85],
      [59, 46, 94],
      [61, 51, 103],
      [63, 55, 112],
      [64, 60, 123],
      [64, 65, 131],
      [64, 70, 139],
      [62, 76, 145],
      [60, 83, 151],
      [58, 89, 154],
      [56, 95, 156],
      [55, 101, 157],
      [54, 107, 159],
      [53, 113, 160],
      [53, 119, 161],
      [52, 125, 163],
      [52, 130, 164],
      [51, 137, 166],
      [51, 142, 167],
      [51, 148, 168],
      [52, 154, 169],
      [53, 159, 170],
      [55, 166, 171],
      [57, 172, 172],
      [60, 177, 172],
      [64, 183, 173],
      [69, 189, 173],
      [75, 195, 173],
      [83, 200, 172],
      [93, 205, 172],
      [106, 209, 172],
      [124, 214, 174],
      [139, 217, 178],
      [153, 221, 182],
      [166, 224, 187],
      [180, 228, 195],
      [191, 232, 203],
      [202, 236, 212],
      [212, 240, 220],
    ],
  },
  flare: {
    kind: 'sns',
    colors: [
      [236, 170, 125],
      [236, 166, 122],
      [235, 161, 119],
      [235, 156, 116],
      [234, 150, 113],
      [234, 145, 110],
      [233, 140, 107],
      [232, 135, 104],
      [232, 129, 101],
      [231, 124, 98],
      [230, 119, 96],
      [229, 115, 95],
      [228, 110, 93],
      [226, 104, 92],
      [224, 99, 91],
      [223, 94, 91],
      [221, 89, 91],
      [218, 84, 92],
      [215, 80, 94],
      [212, 77, 95],
      [208, 73, 97],
      [205, 70, 98],
      [200, 67, 101],
      [196, 65, 102],
      [191, 63, 104],
      [186, 62, 105],
      [181, 60, 107],
      [176, 59, 108],
      [171, 58, 109],
      [167, 57, 110],
      [162, 55, 110],
      [156, 54, 111],
      [152, 53, 112],
      [147, 51, 112],
      [142, 50, 112],
      [137, 49, 112],
      [132, 48, 112],
      [127, 47, 112],
      [123, 46, 111],
      [118, 45, 110],
      [112, 44, 109],
      [108, 43, 108],
      [103, 42, 107],
      [98, 41, 105],
      [93, 40, 104],
      [88, 38, 102],
      [83, 37, 100],
      [79, 36, 99],
    ],
  },
  crest: {
    kind: 'sns',
    colors: [
      [159, 202, 144],
      [153, 199, 145],
      [148, 196, 145],
      [142, 194, 145],
      [136, 190, 145],
      [130, 188, 144],
      [124, 185, 144],
      [119, 183, 144],
      [112, 179, 144],
      [107, 177, 144],
      [103, 174, 144],
      [98, 171, 144],
      [93, 168, 144],
      [88, 165, 144],
      [85, 162, 143],
      [81, 159, 143],
      [77, 156, 143],
      [73, 152, 143],
      [70, 149, 143],
      [66, 147, 142],
      [63, 144, 142],
      [60, 141, 142],
      [56, 137, 141],
      [53, 134, 141],
      [49, 131, 140],
      [46, 128, 140],
      [42, 125, 140],
      [39, 122, 139],
      [36, 119, 139],
      [34, 116, 139],
      [31, 113, 138],
      [29, 109, 138],
      [28, 106, 137],
      [28, 103, 136],
      [28, 99, 136],
      [29, 95, 135],
      [29, 92, 134],
      [31, 89, 133],
      [32, 85, 131],
      [33, 82, 130],
      [35, 78, 128],
      [37, 74, 127],
      [38, 71, 125],
      [39, 67, 123],
      [41, 63, 120],
      [42, 59, 118],
      [43, 56, 117],
      [43, 52, 115],
    ],
  },
  magma: {
    kind: 'sns',
    colors: [
      [1, 1, 11],
      [4, 4, 21],
      [9, 7, 31],
      [14, 10, 42],
      [21, 14, 56],
      [27, 16, 68],
      [34, 17, 80],
      [42, 17, 92],
      [52, 16, 104],
      [60, 15, 113],
      [69, 15, 118],
      [77, 17, 122],
      [85, 19, 125],
      [94, 23, 127],
      [102, 26, 128],
      [110, 30, 129],
      [118, 33, 129],
      [127, 36, 129],
      [135, 39, 129],
      [143, 42, 128],
      [151, 44, 127],
      [159, 47, 126],
      [169, 50, 124],
      [177, 53, 122],
      [185, 55, 120],
      [194, 58, 117],
      [203, 62, 113],
      [211, 66, 109],
      [218, 71, 105],
      [225, 76, 102],
      [231, 82, 98],
      [238, 91, 94],
      [242, 99, 92],
      [245, 108, 91],
      [248, 117, 92],
      [250, 128, 94],
      [251, 138, 98],
      [252, 147, 102],
      [253, 157, 107],
      [254, 166, 113],
      [254, 177, 121],
      [254, 187, 128],
      [254, 196, 136],
      [253, 205, 144],
      [253, 216, 154],
      [253, 225, 163],
      [252, 234, 172],
      [252, 243, 181],
    ],
  },
  viridis: {
    kind: 'sns',
    colors: [
      [69, 8, 91],
      [71, 15, 98],
      [71, 22, 105],
      [72, 29, 111],
      [71, 37, 117],
      [71, 43, 122],
      [70, 49, 126],
      [68, 55, 129],
      [66, 62, 133],
      [64, 68, 135],
      [61, 74, 137],
      [59, 80, 138],
      [57, 85, 139],
      [54, 91, 140],
      [51, 96, 141],
      [49, 101, 141],
      [47, 106, 141],
      [44, 112, 142],
      [42, 117, 142],
      [40, 122, 142],
      [39, 126, 142],
      [37, 131, 141],
      [35, 137, 141],
      [33, 141, 140],
      [31, 146, 140],
      [30, 151, 138],
      [30, 156, 137],
      [31, 161, 135],
      [33, 166, 133],
      [36, 170, 130],
      [41, 175, 127],
      [48, 180, 122],
      [56, 185, 118],
      [64, 189, 114],
      [73, 193, 109],
      [85, 198, 102],
      [96, 201, 96],
      [107, 205, 89],
      [119, 208, 82],
      [131, 211, 75],
      [146, 215, 65],
      [159, 217, 56],
      [173, 220, 48],
      [186, 222, 39],
      [202, 224, 30],
      [215, 226, 25],
      [228, 227, 24],
      [241, 229, 28],
    ],
  },
  vlag: {
    kind: 'sns',
    colors: [
      [49, 110, 188],
      [61, 116, 187],
      [71, 121, 187],
      [81, 126, 188],
      [92, 132, 188],
      [101, 138, 189],
      [109, 143, 191],
      [118, 148, 192],
      [128, 155, 194],
      [136, 160, 196],
      [144, 166, 199],
      [152, 171, 201],
      [160, 177, 204],
      [169, 184, 207],
      [177, 190, 210],
      [185, 196, 214],
      [193, 202, 217],
      [203, 209, 222],
      [211, 215, 226],
      [219, 222, 230],
      [228, 228, 234],
      [236, 235, 239],
      [244, 242, 243],
      [249, 245, 244],
      [250, 244, 243],
      [249, 239, 238],
      [245, 230, 229],
      [241, 222, 221],
      [237, 214, 212],
      [234, 206, 204],
      [230, 198, 196],
      [226, 189, 187],
      [223, 181, 179],
      [220, 173, 171],
      [217, 166, 163],
      [213, 157, 154],
      [210, 149, 146],
      [207, 141, 139],
      [204, 134, 131],
      [201, 127, 124],
      [197, 118, 115],
      [194, 110, 108],
      [190, 103, 101],
      [187, 95, 94],
      [183, 86, 86],
      [179, 79, 79],
      [176, 71, 72],
      [172, 63, 65],
    ],
  },
  icefire: {
    kind: 'sns',
    colors: [
      [175, 220, 215],
      [160, 210, 211],
      [146, 201, 209],
      [131, 192, 207],
      [113, 181, 205],
      [98, 172, 205],
      [85, 163, 205],
      [73, 153, 205],
      [61, 141, 206],
      [55, 131, 207],
      [55, 119, 207],
      [61, 107, 202],
      [68, 96, 192],
      [73, 83, 176],
      [73, 75, 157],
      [69, 68, 138],
      [64, 62, 119],
      [57, 55, 98],
      [51, 49, 82],
      [45, 44, 68],
      [39, 39, 56],
      [35, 34, 46],
      [32, 31, 36],
      [30, 30, 31],
      [33, 29, 30],
      [39, 29, 31],
      [48, 31, 34],
      [58, 33, 38],
      [69, 35, 43],
      [81, 38, 48],
      [94, 40, 54],
      [111, 43, 60],
      [126, 44, 64],
      [141, 45, 67],
      [156, 46, 68],
      [173, 49, 66],
      [187, 53, 63],
      [199, 61, 58],
      [209, 70, 54],
      [218, 82, 52],
      [227, 97, 51],
      [233, 111, 54],
      [237, 125, 63],
      [240, 140, 78],
      [244, 157, 98],
      [246, 170, 116],
      [249, 184, 135],
      [252, 198, 153],
    ],
  },
  Blues: {
    kind: 'sns',
    colors: [
      [243, 248, 253],
      [241, 247, 252],
      [239, 246, 252],
      [237, 245, 251],
      [235, 244, 251],
      [233, 243, 250],
      [231, 242, 250],
      [229, 241, 249],
      [227, 240, 249],
      [225, 239, 248],
      [222, 237, 247],
      [219, 235, 246],
      [216, 233, 245],
      [213, 231, 244],
      [210, 229, 243],
      [206, 226, 241],
      [202, 223, 239],
      [198, 220, 237],
      [194, 217, 235],
      [190, 214, 233],
      [185, 210, 230],
      [180, 206, 227],
      [174, 202, 224],
      [168, 198, 221],
      [162, 194, 218],
      [155, 189, 214],
      [148, 184, 210],
      [140, 178, 206],
      [132, 172, 202],
      [123, 165, 197],
      [114, 158, 192],
      [104, 150, 186],
      [94, 142, 180],
      [83, 133, 173],
      [72, 124, 166],
      [60, 114, 158],
      [48, 104, 150],
      [38, 95, 145],
      [30, 88, 142],
      [24, 82, 140],
      [20, 78, 138],
      [18, 75, 136],
      [16, 72, 135],
      [15, 70, 134],
      [14, 68, 133],
      [14, 66, 133],
      [14, 64, 132],
      [14, 82, 152],
    ],
  },
  YlOrBr: {
    kind: 'sns',
    colors: [
      [255, 253, 222],
      [255, 252, 216],
      [255, 251, 209],
      [255, 249, 203],
      [255, 248, 195],
      [255, 247, 189],
      [254, 244, 182],
      [254, 241, 175],
      [254, 237, 167],
      [254, 234, 160],
      [254, 231, 154],
      [254, 228, 147],
      [254, 223, 138],
      [254, 218, 125],
      [254, 213, 115],
      [254, 208, 105],
      [254, 203, 94],
      [254, 197, 82],
      [254, 191, 74],
      [254, 184, 69],
      [254, 177, 63],
      [254, 171, 57],
      [254, 163, 49],
      [254, 156, 43],
      [252, 149, 39],
      [249, 143, 36],
      [246, 135, 32],
      [243, 129, 28],
      [240, 122, 25],
      [237, 116, 22],
      [234, 110, 19],
      [228, 103, 15],
      [223, 97, 12],
      [218, 92, 10],
      [213, 86, 7],
      [207, 79, 3],
      [201, 74, 2],
      [193, 70, 2],
      [185, 67, 2],
      [177, 63, 3],
      [167, 58, 3],
      [159, 55, 3],
      [151, 51, 4],
      [143, 49, 4],
      [134, 46, 4],
      [126, 44, 5],
      [118, 41, 5],
      [110, 39, 5],
    ],
  },
  Spectral: {
    kind: 'sns',
    colors: [
      [168, 12, 68],
      [179, 24, 71],
      [190, 36, 73],
      [201, 48, 76],
      [213, 62, 78],
      [219, 72, 76],
      [225, 81, 74],
      [231, 90, 71],
      [239, 101, 68],
      [244, 111, 68],
      [246, 124, 74],
      [247, 137, 79],
      [249, 149, 85],
      [251, 165, 92],
      [253, 176, 99],
      [253, 186, 107],
      [253, 196, 115],
      [253, 208, 125],
      [253, 218, 134],
      [254, 226, 143],
      [254, 232, 153],
      [254, 238, 163],
      [254, 245, 175],
      [254, 251, 185],
      [252, 254, 187],
      [247, 252, 179],
      [241, 249, 170],
      [236, 247, 162],
      [231, 245, 155],
      [223, 242, 153],
      [211, 237, 155],
      [197, 231, 158],
      [186, 227, 160],
      [174, 222, 163],
      [161, 217, 164],
      [145, 210, 164],
      [131, 205, 164],
      [118, 200, 164],
      [104, 195, 164],
      [93, 184, 168],
      [81, 171, 174],
      [71, 159, 179],
      [61, 148, 183],
      [51, 137, 188],
      [59, 123, 183],
      [68, 112, 177],
      [76, 101, 172],
      [85, 90, 167],
    ],
  },
  Spectral_a0: {
    kind: 'sns',
    colors: [
      [168, 12, 68, 0],
      [179, 24, 71, 255],
      [190, 36, 73, 255],
      [201, 48, 76, 255],
      [213, 62, 78, 255],
      [219, 72, 76, 255],
      [225, 81, 74, 255],
      [231, 90, 71, 255],
      [239, 101, 68, 255],
      [244, 111, 68, 255],
      [246, 124, 74, 255],
      [247, 137, 79, 255],
      [249, 149, 85, 255],
      [251, 165, 92, 255],
      [253, 176, 99, 255],
      [253, 186, 107, 255],
      [253, 196, 115, 255],
      [253, 208, 125, 255],
      [253, 218, 134, 255],
      [254, 226, 143, 255],
      [254, 232, 153, 255],
      [254, 238, 163, 255],
      [254, 245, 175, 255],
      [254, 251, 185, 255],
      [252, 254, 187, 255],
      [247, 252, 179, 255],
      [241, 249, 170, 255],
      [236, 247, 162, 255],
      [231, 245, 155, 255],
      [223, 242, 153, 255],
      [211, 237, 155, 255],
      [197, 231, 158, 255],
      [186, 227, 160, 255],
      [174, 222, 163, 255],
      [161, 217, 164, 255],
      [145, 210, 164, 255],
      [131, 205, 164, 255],
      [118, 200, 164, 255],
      [104, 195, 164, 255],
      [93, 184, 168, 255],
      [81, 171, 174, 255],
      [71, 159, 179, 255],
      [61, 148, 183, 255],
      [51, 137, 188, 255],
      [59, 123, 183, 255],
      [68, 112, 177, 255],
      [76, 101, 172, 255],
      [85, 90, 167, 255],
    ],
  },
  coolwarm: {
    kind: 'sns',
    colors: [
      [64, 84, 199],
      [70, 93, 207],
      [76, 102, 214],
      [82, 110, 220],
      [90, 120, 227],
      [96, 128, 232],
      [103, 136, 237],
      [109, 144, 241],
      [117, 152, 246],
      [124, 160, 249],
      [131, 166, 251],
      [138, 173, 253],
      [145, 179, 254],
      [153, 186, 254],
      [160, 191, 254],
      [167, 196, 253],
      [174, 201, 252],
      [182, 206, 249],
      [188, 209, 246],
      [194, 212, 243],
      [200, 215, 239],
      [206, 217, 235],
      [213, 219, 229],
      [218, 220, 223],
      [223, 219, 217],
      [228, 216, 209],
      [233, 212, 201],
      [237, 208, 193],
      [240, 204, 185],
      [242, 199, 178],
      [244, 194, 170],
      [246, 187, 160],
      [247, 181, 152],
      [247, 174, 145],
      [246, 167, 137],
      [245, 158, 127],
      [243, 150, 120],
      [241, 142, 112],
      [238, 134, 105],
      [234, 125, 97],
      [230, 114, 89],
      [225, 104, 82],
      [220, 94, 75],
      [215, 84, 68],
      [207, 70, 61],
      [201, 59, 55],
      [194, 45, 49],
      [187, 26, 43],
    ],
  },
  plasma: {
    kind: 'sns',
    colors: [
      [27, 6, 140],
      [37, 5, 145],
      [47, 4, 149],
      [56, 4, 153],
      [66, 3, 157],
      [74, 2, 160],
      [82, 1, 163],
      [90, 0, 165],
      [100, 0, 167],
      [108, 0, 168],
      [115, 0, 168],
      [123, 2, 168],
      [130, 4, 167],
      [139, 9, 164],
      [146, 15, 162],
      [153, 20, 159],
      [159, 26, 155],
      [167, 33, 151],
      [173, 38, 146],
      [178, 44, 142],
      [184, 50, 137],
      [189, 55, 132],
      [195, 62, 127],
      [200, 68, 122],
      [205, 73, 117],
      [209, 79, 113],
      [215, 86, 108],
      [219, 91, 103],
      [223, 97, 99],
      [227, 103, 95],
      [230, 109, 90],
      [234, 116, 85],
      [237, 123, 81],
      [240, 129, 77],
      [243, 135, 72],
      [246, 143, 67],
      [248, 150, 63],
      [250, 157, 58],
      [251, 164, 54],
      [252, 172, 50],
      [253, 181, 45],
      [253, 188, 42],
      [253, 196, 39],
      [252, 204, 37],
      [250, 214, 36],
      [248, 223, 36],
      [245, 231, 38],
      [242, 240, 38],
    ],
  },
  cividis: {
    kind: 'sns',
    colors: [
      [0, 38, 85],
      [0, 41, 94],
      [0, 44, 103],
      [0, 48, 112],
      [11, 51, 112],
      [24, 55, 111],
      [33, 59, 110],
      [40, 62, 109],
      [48, 66, 108],
      [54, 70, 108],
      [59, 73, 107],
      [65, 77, 107],
      [70, 80, 107],
      [76, 84, 108],
      [80, 88, 108],
      [85, 91, 109],
      [89, 95, 109],
      [95, 99, 110],
      [99, 102, 111],
      [104, 106, 112],
      [108, 109, 114],
      [112, 113, 115],
      [117, 117, 117],
      [121, 121, 119],
      [126, 125, 120],
      [131, 128, 120],
      [136, 133, 120],
      [141, 137, 120],
      [146, 140, 119],
      [151, 144, 118],
      [156, 148, 118],
      [162, 153, 116],
      [167, 157, 115],
      [172, 161, 113],
      [177, 165, 112],
      [183, 170, 109],
      [188, 174, 107],
      [194, 178, 105],
      [199, 182, 102],
      [204, 187, 99],
      [211, 192, 95],
      [216, 196, 91],
      [222, 201, 87],
      [227, 205, 82],
      [234, 211, 76],
      [239, 216, 70],
      [245, 220, 63],
      [251, 225, 54],
    ],
  },
  turbo: {
    kind: 'sns',
    colors: [
      [54, 33, 95],
      [59, 47, 127],
      [63, 61, 156],
      [66, 75, 181],
      [69, 91, 206],
      [70, 104, 224],
      [70, 117, 237],
      [70, 130, 248],
      [66, 145, 254],
      [60, 157, 253],
      [52, 170, 248],
      [43, 182, 239],
      [35, 194, 228],
      [27, 207, 212],
      [23, 217, 199],
      [24, 225, 186],
      [30, 232, 175],
      [44, 239, 157],
      [59, 244, 141],
      [77, 249, 124],
      [97, 252, 108],
      [116, 254, 92],
      [139, 254, 75],
      [155, 253, 64],
      [169, 251, 57],
      [182, 247, 53],
      [197, 239, 51],
      [209, 232, 52],
      [221, 224, 54],
      [231, 215, 56],
      [239, 205, 57],
      [247, 192, 57],
      [251, 181, 55],
      [253, 169, 50],
      [254, 155, 45],
      [252, 137, 38],
      [250, 122, 31],
      [246, 107, 24],
      [241, 93, 19],
      [234, 80, 13],
      [226, 66, 9],
      [217, 56, 6],
      [208, 47, 4],
      [197, 38, 2],
      [182, 28, 1],
      [169, 21, 1],
      [154, 14, 1],
      [139, 9, 1],
    ],
  },
  rocket_r: {
    kind: 'sns',
    colors: [
      [249, 227, 209],
      [248, 219, 198],
      [247, 212, 187],
      [247, 205, 176],
      [246, 195, 163],
      [246, 188, 153],
      [246, 180, 142],
      [245, 172, 133],
      [245, 162, 122],
      [245, 154, 113],
      [245, 146, 105],
      [244, 137, 97],
      [244, 128, 89],
      [243, 118, 81],
      [242, 109, 74],
      [241, 99, 69],
      [239, 89, 64],
      [236, 78, 62],
      [233, 68, 61],
      [230, 59, 63],
      [225, 50, 66],
      [219, 42, 69],
      [212, 34, 74],
      [205, 28, 77],
      [198, 24, 81],
      [191, 22, 84],
      [181, 22, 87],
      [172, 23, 89],
      [164, 25, 90],
      [155, 26, 91],
      [147, 28, 91],
      [137, 29, 90],
      [128, 30, 90],
      [120, 30, 88],
      [111, 31, 87],
      [102, 30, 84],
      [93, 30, 81],
      [85, 29, 78],
      [78, 29, 75],
      [70, 28, 71],
      [61, 26, 66],
      [53, 24, 61],
      [46, 22, 56],
      [38, 20, 51],
      [30, 17, 45],
      [22, 14, 40],
      [15, 11, 35],
      [8, 7, 30],
    ],
  },
  mako_r: {
    kind: 'sns',
    colors: [
      [212, 240, 220],
      [202, 236, 212],
      [191, 232, 203],
      [180, 228, 195],
      [166, 224, 187],
      [153, 221, 182],
      [139, 217, 178],
      [124, 214, 174],
      [106, 209, 172],
      [93, 205, 172],
      [83, 200, 172],
      [75, 195, 173],
      [69, 189, 173],
      [64, 183, 173],
      [60, 177, 172],
      [57, 172, 172],
      [55, 166, 171],
      [53, 159, 170],
      [52, 154, 169],
      [51, 148, 168],
      [51, 142, 167],
      [51, 137, 166],
      [52, 130, 164],
      [52, 125, 163],
      [53, 119, 161],
      [53, 113, 160],
      [54, 107, 159],
      [55, 101, 157],
      [56, 95, 156],
      [58, 89, 154],
      [60, 83, 151],
      [62, 76, 145],
      [64, 70, 139],
      [64, 65, 131],
      [64, 60, 123],
      [63, 55, 112],
      [61, 51, 103],
      [59, 46, 94],
      [56, 42, 85],
      [54, 38, 77],
      [50, 34, 67],
      [46, 30, 58],
      [42, 26, 50],
      [37, 23, 43],
      [31, 18, 33],
      [26, 15, 26],
      [21, 11, 19],
      [16, 7, 11],
    ],
  },
  flare_r: {
    kind: 'sns',
    colors: [
      [79, 36, 99],
      [83, 37, 100],
      [88, 38, 102],
      [93, 40, 104],
      [98, 41, 105],
      [103, 42, 107],
      [108, 43, 108],
      [112, 44, 109],
      [118, 45, 110],
      [123, 46, 111],
      [127, 47, 112],
      [132, 48, 112],
      [137, 49, 112],
      [142, 50, 112],
      [147, 51, 112],
      [152, 53, 112],
      [156, 54, 111],
      [162, 55, 110],
      [167, 57, 110],
      [171, 58, 109],
      [176, 59, 108],
      [181, 60, 107],
      [186, 62, 105],
      [191, 63, 104],
      [196, 65, 102],
      [200, 67, 101],
      [205, 70, 98],
      [208, 73, 97],
      [212, 77, 95],
      [215, 80, 94],
      [218, 84, 92],
      [221, 89, 91],
      [223, 94, 91],
      [224, 99, 91],
      [226, 104, 92],
      [228, 110, 93],
      [229, 115, 95],
      [230, 119, 96],
      [231, 124, 98],
      [232, 129, 101],
      [232, 135, 104],
      [233, 140, 107],
      [234, 145, 110],
      [234, 150, 113],
      [235, 156, 116],
      [235, 161, 119],
      [236, 166, 122],
      [236, 170, 125],
    ],
  },
  crest_r: {
    kind: 'sns',
    colors: [
      [43, 52, 115],
      [43, 56, 117],
      [42, 59, 118],
      [41, 63, 120],
      [39, 67, 123],
      [38, 71, 125],
      [37, 74, 127],
      [35, 78, 128],
      [33, 82, 130],
      [32, 85, 131],
      [31, 89, 133],
      [29, 92, 134],
      [29, 95, 135],
      [28, 99, 136],
      [28, 103, 136],
      [28, 106, 137],
      [29, 109, 138],
      [31, 113, 138],
      [34, 116, 139],
      [36, 119, 139],
      [39, 122, 139],
      [42, 125, 140],
      [46, 128, 140],
      [49, 131, 140],
      [53, 134, 141],
      [56, 137, 141],
      [60, 141, 142],
      [63, 144, 142],
      [66, 147, 142],
      [70, 149, 143],
      [73, 152, 143],
      [77, 156, 143],
      [81, 159, 143],
      [85, 162, 143],
      [88, 165, 144],
      [93, 168, 144],
      [98, 171, 144],
      [103, 174, 144],
      [107, 177, 144],
      [112, 179, 144],
      [119, 183, 144],
      [124, 185, 144],
      [130, 188, 144],
      [136, 190, 145],
      [142, 194, 145],
      [148, 196, 145],
      [153, 199, 145],
      [159, 202, 144],
    ],
  },
  magma_r: {
    kind: 'sns',
    colors: [
      [252, 243, 181],
      [252, 234, 172],
      [253, 225, 163],
      [253, 216, 154],
      [253, 205, 144],
      [254, 196, 136],
      [254, 187, 128],
      [254, 177, 121],
      [254, 166, 113],
      [253, 157, 107],
      [252, 147, 102],
      [251, 138, 98],
      [250, 128, 94],
      [248, 117, 92],
      [245, 108, 91],
      [242, 99, 92],
      [238, 91, 94],
      [231, 82, 98],
      [225, 76, 102],
      [218, 71, 105],
      [211, 66, 109],
      [203, 62, 113],
      [194, 58, 117],
      [185, 55, 120],
      [177, 53, 122],
      [169, 50, 124],
      [159, 47, 126],
      [151, 44, 127],
      [143, 42, 128],
      [135, 39, 129],
      [127, 36, 129],
      [118, 33, 129],
      [110, 30, 129],
      [102, 26, 128],
      [94, 23, 127],
      [85, 19, 125],
      [77, 17, 122],
      [69, 15, 118],
      [60, 15, 113],
      [52, 16, 104],
      [42, 17, 92],
      [34, 17, 80],
      [27, 16, 68],
      [21, 14, 56],
      [14, 10, 42],
      [9, 7, 31],
      [4, 4, 21],
      [1, 1, 11],
    ],
  },
  viridis_r: {
    kind: 'sns',
    colors: [
      [241, 229, 28],
      [228, 227, 24],
      [215, 226, 25],
      [202, 224, 30],
      [186, 222, 39],
      [173, 220, 48],
      [159, 217, 56],
      [146, 215, 65],
      [131, 211, 75],
      [119, 208, 82],
      [107, 205, 89],
      [96, 201, 96],
      [85, 198, 102],
      [73, 193, 109],
      [64, 189, 114],
      [56, 185, 118],
      [48, 180, 122],
      [41, 175, 127],
      [36, 170, 130],
      [33, 166, 133],
      [31, 161, 135],
      [30, 156, 137],
      [30, 151, 138],
      [31, 146, 140],
      [33, 141, 140],
      [35, 137, 141],
      [37, 131, 141],
      [39, 126, 142],
      [40, 122, 142],
      [42, 117, 142],
      [44, 112, 142],
      [47, 106, 141],
      [49, 101, 141],
      [51, 96, 141],
      [54, 91, 140],
      [57, 85, 139],
      [59, 80, 138],
      [61, 74, 137],
      [64, 68, 135],
      [66, 62, 133],
      [68, 55, 129],
      [70, 49, 126],
      [71, 43, 122],
      [71, 37, 117],
      [72, 29, 111],
      [71, 22, 105],
      [71, 15, 98],
      [69, 8, 91],
    ],
  },
  vlag_r: {
    kind: 'sns',
    colors: [
      [172, 63, 65],
      [176, 71, 72],
      [179, 79, 79],
      [183, 86, 86],
      [187, 95, 94],
      [190, 103, 101],
      [194, 110, 108],
      [197, 118, 115],
      [201, 127, 124],
      [204, 134, 131],
      [207, 141, 139],
      [210, 149, 146],
      [213, 157, 154],
      [217, 166, 163],
      [220, 173, 171],
      [223, 181, 179],
      [226, 189, 187],
      [230, 198, 196],
      [234, 206, 204],
      [237, 214, 212],
      [241, 222, 221],
      [245, 230, 229],
      [249, 239, 238],
      [250, 244, 243],
      [249, 245, 244],
      [244, 242, 243],
      [236, 235, 239],
      [228, 228, 234],
      [219, 222, 230],
      [211, 215, 226],
      [203, 209, 222],
      [193, 202, 217],
      [185, 196, 214],
      [177, 190, 210],
      [169, 184, 207],
      [160, 177, 204],
      [152, 171, 201],
      [144, 166, 199],
      [136, 160, 196],
      [128, 155, 194],
      [118, 148, 192],
      [109, 143, 191],
      [101, 138, 189],
      [92, 132, 188],
      [81, 126, 188],
      [71, 121, 187],
      [61, 116, 187],
      [49, 110, 188],
    ],
  },
  icefire_r: {
    kind: 'sns',
    colors: [
      [252, 198, 153],
      [249, 184, 135],
      [246, 170, 116],
      [244, 157, 98],
      [240, 140, 78],
      [237, 125, 63],
      [233, 111, 54],
      [227, 97, 51],
      [218, 82, 52],
      [209, 70, 54],
      [199, 61, 58],
      [187, 53, 63],
      [173, 49, 66],
      [156, 46, 68],
      [141, 45, 67],
      [126, 44, 64],
      [111, 43, 60],
      [94, 40, 54],
      [81, 38, 48],
      [69, 35, 43],
      [58, 33, 38],
      [48, 31, 34],
      [39, 29, 31],
      [33, 29, 30],
      [30, 30, 31],
      [32, 31, 36],
      [35, 34, 46],
      [39, 39, 56],
      [45, 44, 68],
      [51, 49, 82],
      [57, 55, 98],
      [64, 62, 119],
      [69, 68, 138],
      [73, 75, 157],
      [73, 83, 176],
      [68, 96, 192],
      [61, 107, 202],
      [55, 119, 207],
      [55, 131, 207],
      [61, 141, 206],
      [73, 153, 205],
      [85, 163, 205],
      [98, 172, 205],
      [113, 181, 205],
      [131, 192, 207],
      [146, 201, 209],
      [160, 210, 211],
      [175, 220, 215],
    ],
  },
  Blues_r: {
    kind: 'sns',
    colors: [
      [8, 53, 114],
      [8, 58, 122],
      [8, 63, 130],
      [8, 68, 137],
      [8, 74, 146],
      [8, 80, 154],
      [11, 85, 159],
      [15, 90, 163],
      [19, 96, 167],
      [23, 101, 171],
      [27, 106, 175],
      [31, 111, 179],
      [36, 116, 182],
      [42, 122, 185],
      [47, 127, 188],
      [52, 132, 191],
      [58, 138, 193],
      [64, 144, 197],
      [70, 148, 199],
      [76, 153, 202],
      [83, 157, 204],
      [89, 162, 207],
      [97, 167, 210],
      [103, 171, 212],
      [111, 176, 214],
      [119, 180, 216],
      [128, 185, 218],
      [136, 190, 220],
      [144, 194, 222],
      [152, 199, 223],
      [160, 202, 225],
      [167, 206, 228],
      [173, 208, 230],
      [180, 211, 232],
      [186, 214, 234],
      [193, 217, 237],
      [199, 219, 239],
      [203, 222, 240],
      [206, 224, 241],
      [210, 227, 243],
      [215, 230, 244],
      [218, 232, 245],
      [222, 235, 247],
      [226, 237, 248],
      [231, 240, 249],
      [235, 243, 251],
      [239, 245, 252],
      [243, 248, 253],
    ],
  },
  YlOrBr_r: {
    kind: 'sns',
    colors: [
      [110, 39, 5],
      [118, 41, 5],
      [126, 44, 5],
      [134, 46, 4],
      [143, 49, 4],
      [151, 51, 4],
      [159, 55, 3],
      [167, 58, 3],
      [177, 63, 3],
      [185, 67, 2],
      [193, 70, 2],
      [201, 74, 2],
      [207, 79, 3],
      [213, 86, 7],
      [218, 92, 10],
      [223, 97, 12],
      [228, 103, 15],
      [234, 110, 19],
      [237, 116, 22],
      [240, 122, 25],
      [243, 129, 28],
      [246, 135, 32],
      [249, 143, 36],
      [252, 149, 39],
      [254, 156, 43],
      [254, 163, 49],
      [254, 171, 57],
      [254, 177, 63],
      [254, 184, 69],
      [254, 191, 74],
      [254, 197, 82],
      [254, 203, 94],
      [254, 208, 105],
      [254, 213, 115],
      [254, 218, 125],
      [254, 223, 138],
      [254, 228, 147],
      [254, 231, 154],
      [254, 234, 160],
      [254, 237, 167],
      [254, 241, 175],
      [254, 244, 182],
      [255, 247, 189],
      [255, 248, 195],
      [255, 249, 203],
      [255, 251, 209],
      [255, 252, 216],
      [255, 253, 222],
    ],
  },
  Spectral_r: {
    kind: 'sns',
    colors: [
      [85, 90, 167],
      [76, 101, 172],
      [68, 112, 177],
      [59, 123, 183],
      [51, 137, 188],
      [61, 148, 183],
      [71, 159, 179],
      [81, 171, 174],
      [93, 184, 168],
      [104, 195, 164],
      [118, 200, 164],
      [131, 205, 164],
      [145, 210, 164],
      [161, 217, 164],
      [174, 222, 163],
      [186, 227, 160],
      [197, 231, 158],
      [211, 237, 155],
      [223, 242, 153],
      [231, 245, 155],
      [236, 247, 162],
      [241, 249, 170],
      [247, 252, 179],
      [252, 254, 187],
      [254, 251, 185],
      [254, 245, 175],
      [254, 238, 163],
      [254, 232, 153],
      [254, 226, 143],
      [253, 218, 134],
      [253, 208, 125],
      [253, 196, 115],
      [253, 186, 107],
      [253, 176, 99],
      [251, 165, 92],
      [249, 149, 85],
      [247, 137, 79],
      [246, 124, 74],
      [244, 111, 68],
      [239, 101, 68],
      [231, 90, 71],
      [225, 81, 74],
      [219, 72, 76],
      [213, 62, 78],
      [201, 48, 76],
      [190, 36, 73],
      [179, 24, 71],
      [168, 12, 68],
    ],
  },
  Spectral_r_a0: {
    kind: 'sns',
    colors: [
      [85, 90, 167, 0],
      [76, 101, 172, 255],
      [68, 112, 177, 255],
      [59, 123, 183, 255],
      [51, 137, 188, 255],
      [61, 148, 183, 255],
      [71, 159, 179, 255],
      [81, 171, 174, 255],
      [93, 184, 168, 255],
      [104, 195, 164, 255],
      [118, 200, 164, 255],
      [131, 205, 164, 255],
      [145, 210, 164, 255],
      [161, 217, 164, 255],
      [174, 222, 163, 255],
      [186, 227, 160, 255],
      [197, 231, 158, 255],
      [211, 237, 155, 255],
      [223, 242, 153, 255],
      [231, 245, 155, 255],
      [236, 247, 162, 255],
      [241, 249, 170, 255],
      [247, 252, 179, 255],
      [252, 254, 187, 255],
      [254, 251, 185, 255],
      [254, 245, 175, 255],
      [254, 238, 163, 255],
      [254, 232, 153, 255],
      [254, 226, 143, 255],
      [253, 218, 134, 255],
      [253, 208, 125, 255],
      [253, 196, 115, 255],
      [253, 186, 107, 255],
      [253, 176, 99, 255],
      [251, 165, 92, 255],
      [249, 149, 85, 255],
      [247, 137, 79, 255],
      [246, 124, 74, 255],
      [244, 111, 68, 255],
      [239, 101, 68, 255],
      [231, 90, 71, 255],
      [225, 81, 74, 255],
      [219, 72, 76, 255],
      [213, 62, 78, 255],
      [201, 48, 76, 255],
      [190, 36, 73, 255],
      [179, 24, 71, 255],
      [168, 12, 68, 255],
    ],
  },
  coolwarm_r: {
    kind: 'sns',
    colors: [
      [187, 26, 43],
      [194, 45, 49],
      [201, 59, 55],
      [207, 70, 61],
      [215, 84, 68],
      [220, 94, 75],
      [225, 104, 82],
      [230, 114, 89],
      [234, 125, 97],
      [238, 134, 105],
      [241, 142, 112],
      [243, 150, 120],
      [245, 158, 127],
      [246, 167, 137],
      [247, 174, 145],
      [247, 181, 152],
      [246, 187, 160],
      [244, 194, 170],
      [242, 199, 178],
      [240, 204, 185],
      [237, 208, 193],
      [233, 212, 201],
      [228, 216, 209],
      [223, 219, 217],
      [218, 220, 223],
      [213, 219, 229],
      [206, 217, 235],
      [200, 215, 239],
      [194, 212, 243],
      [188, 209, 246],
      [182, 206, 249],
      [174, 201, 252],
      [167, 196, 253],
      [160, 191, 254],
      [153, 186, 254],
      [145, 179, 254],
      [138, 173, 253],
      [131, 166, 251],
      [124, 160, 249],
      [117, 152, 246],
      [109, 144, 241],
      [103, 136, 237],
      [96, 128, 232],
      [90, 120, 227],
      [82, 110, 220],
      [76, 102, 214],
      [70, 93, 207],
      [64, 84, 199],
    ],
  },
  plasma_r: {
    kind: 'sns',
    colors: [
      [242, 240, 38],
      [245, 231, 38],
      [248, 223, 36],
      [250, 214, 36],
      [252, 204, 37],
      [253, 196, 39],
      [253, 188, 42],
      [253, 181, 45],
      [252, 172, 50],
      [251, 164, 54],
      [250, 157, 58],
      [248, 150, 63],
      [246, 143, 67],
      [243, 135, 72],
      [240, 129, 77],
      [237, 123, 81],
      [234, 116, 85],
      [230, 109, 90],
      [227, 103, 95],
      [223, 97, 99],
      [219, 91, 103],
      [215, 86, 108],
      [209, 79, 113],
      [205, 73, 117],
      [200, 68, 122],
      [195, 62, 127],
      [189, 55, 132],
      [184, 50, 137],
      [178, 44, 142],
      [173, 38, 146],
      [167, 33, 151],
      [159, 26, 155],
      [153, 20, 159],
      [146, 15, 162],
      [139, 9, 164],
      [130, 4, 167],
      [123, 2, 168],
      [115, 0, 168],
      [108, 0, 168],
      [100, 0, 167],
      [90, 0, 165],
      [82, 1, 163],
      [74, 2, 160],
      [66, 3, 157],
      [56, 4, 153],
      [47, 4, 149],
      [37, 5, 145],
      [27, 6, 140],
    ],
  },
  cividis_r: {
    kind: 'sns',
    colors: [
      [251, 225, 54],
      [245, 220, 63],
      [239, 216, 70],
      [234, 211, 76],
      [227, 205, 82],
      [222, 201, 87],
      [216, 196, 91],
      [211, 192, 95],
      [204, 187, 99],
      [199, 182, 102],
      [194, 178, 105],
      [188, 174, 107],
      [183, 170, 109],
      [177, 165, 112],
      [172, 161, 113],
      [167, 157, 115],
      [162, 153, 116],
      [156, 148, 118],
      [151, 144, 118],
      [146, 140, 119],
      [141, 137, 120],
      [136, 133, 120],
      [131, 128, 120],
      [126, 125, 120],
      [121, 121, 119],
      [117, 117, 117],
      [112, 113, 115],
      [108, 109, 114],
      [104, 106, 112],
      [99, 102, 111],
      [95, 99, 110],
      [89, 95, 109],
      [85, 91, 109],
      [80, 88, 108],
      [76, 84, 108],
      [70, 80, 107],
      [65, 77, 107],
      [59, 73, 107],
      [54, 70, 108],
      [48, 66, 108],
      [40, 62, 109],
      [33, 59, 110],
      [24, 55, 111],
      [11, 51, 112],
      [0, 48, 112],
      [0, 44, 103],
      [0, 41, 94],
      [0, 38, 85],
    ],
  },
  turbo_r: {
    kind: 'sns',
    colors: [
      [139, 9, 1],
      [154, 14, 1],
      [169, 21, 1],
      [182, 28, 1],
      [197, 38, 2],
      [208, 47, 4],
      [217, 56, 6],
      [226, 66, 9],
      [234, 80, 13],
      [241, 93, 19],
      [246, 107, 24],
      [250, 122, 31],
      [252, 137, 38],
      [254, 155, 45],
      [253, 169, 50],
      [251, 181, 55],
      [247, 192, 57],
      [239, 205, 57],
      [231, 215, 56],
      [221, 224, 54],
      [209, 232, 52],
      [197, 239, 51],
      [182, 247, 53],
      [169, 251, 57],
      [155, 253, 64],
      [139, 254, 75],
      [116, 254, 92],
      [97, 252, 108],
      [77, 249, 124],
      [59, 244, 141],
      [44, 239, 157],
      [30, 232, 175],
      [24, 225, 186],
      [23, 217, 199],
      [27, 207, 212],
      [35, 194, 228],
      [43, 182, 239],
      [52, 170, 248],
      [60, 157, 253],
      [66, 145, 254],
      [70, 130, 248],
      [70, 117, 237],
      [70, 104, 224],
      [69, 91, 206],
      [66, 75, 181],
      [63, 61, 156],
      [59, 47, 127],
      [54, 33, 95],
    ],
  },
  color_alpha: {
    kind: 'color_alpha_scale',
    alphas: [
      0, 5, 10, 16, 21, 27, 32, 37, 43, 48, 54, 59, 65, 70, 75, 81, 86, 92, 97, 103, 108, 113, 119,
      124, 130, 135, 141, 146, 151, 157, 162, 168, 173, 179, 184, 189, 195, 200, 206, 211, 217, 222,
      227, 233, 238, 244, 249, 255,
    ],
  },
};
